/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AdminProfile } from '../models/AdminProfile';
import type { AvailableWard } from '../models/AvailableWard';
import type { EpisodeRelativeTrigger } from '../models/EpisodeRelativeTrigger';
import type { PaginatedEpisodeRelativeTriggerList } from '../models/PaginatedEpisodeRelativeTriggerList';
import type { PaginatedReadOnlyShortAdminSurveyResultList } from '../models/PaginatedReadOnlyShortAdminSurveyResultList';
import type { PaginatedReadOnlyShortSurveyTemplateList } from '../models/PaginatedReadOnlyShortSurveyTemplateList';
import type { PaginatedRecurringTriggerList } from '../models/PaginatedRecurringTriggerList';
import type { PaginatedSurveyAssignmentRuleList } from '../models/PaginatedSurveyAssignmentRuleList';
import type { PatchedEpisodeRelativeTrigger } from '../models/PatchedEpisodeRelativeTrigger';
import type { PatchedRecurringTrigger } from '../models/PatchedRecurringTrigger';
import type { PatchedSurveyAssignmentRule } from '../models/PatchedSurveyAssignmentRule';
import type { PatchedSurveyTemplateSerializerV2 } from '../models/PatchedSurveyTemplateSerializerV2';
import type { RecurringTrigger } from '../models/RecurringTrigger';
import type { SurveyAssignmentPreviewResponse } from '../models/SurveyAssignmentPreviewResponse';
import type { SurveyAssignmentRule } from '../models/SurveyAssignmentRule';
import type { SurveyTemplateSerializerV2 } from '../models/SurveyTemplateSerializerV2';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class AdminService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Upload a file to the server.
     * @returns any File uploaded successfully
     * @throws ApiError
     */
    public fileUpload({
        formData,
    }: {
        formData?: {
            /**
             * The file to upload
             */
            file: Blob;
        },
    }): CancelablePromise<{
        location?: string;
    }> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/admin/tinymce/file-upload/',
            formData: formData,
            mediaType: 'multipart/form-data',
            errors: {
                400: `Failed to upload file`,
            },
        });
    }

    /**
     * TinyMCE file browser script.
     * @returns string JavaScript file for TinyMCE file browser
     * @throws ApiError
     */
    public tinymceFileBrowser(): CancelablePromise<string> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/admin/tinymce/filebrowser/',
        });
    }

    /**
     * @returns AdminProfile
     * @throws ApiError
     */
    public adminMeRetrieve(): CancelablePromise<AdminProfile> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/admin/me/',
        });
    }

    /**
     * @returns PaginatedReadOnlyShortAdminSurveyResultList
     * @throws ApiError
     */
    public adminSurveyTemplatesResultsList({
        templateId,
        clinic,
        limit,
        offset,
        ordering,
        requestedAtAfter,
        requestedAtBefore,
        search,
        status,
        statusUpdatedAtAfter,
        statusUpdatedAtBefore,
        ward,
    }: {
        templateId: number,
        /**
         * Clinic
         */
        clinic?: Array<number>,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
        /**
         * Which field to use when ordering the results.
         */
        ordering?: string,
        /**
         * Date of the survey were requested.
         */
        requestedAtAfter?: string,
        /**
         * Date of the survey were requested.
         */
        requestedAtBefore?: string,
        /**
         * A search term.
         */
        search?: string,
        /**
         * Status
         *
         * * `in_progress` - In Progress
         * * `completed` - Completed
         * * `requested` - Requested
         * * `canceled` - Canceled
         * * `overdue` - Overdue
         */
        status?: Array<'canceled' | 'completed' | 'in_progress' | 'overdue' | 'requested'>,
        /**
         * Date of the survey were completed, canceled or requested.
         */
        statusUpdatedAtAfter?: string,
        /**
         * Date of the survey were completed, canceled or requested.
         */
        statusUpdatedAtBefore?: string,
        /**
         * Ward
         */
        ward?: Array<number>,
    }): CancelablePromise<PaginatedReadOnlyShortAdminSurveyResultList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/admin/survey-templates/{template_id}/results/',
            path: {
                'template_id': templateId,
            },
            query: {
                'clinic': clinic,
                'limit': limit,
                'offset': offset,
                'ordering': ordering,
                'requested_at_after': requestedAtAfter,
                'requested_at_before': requestedAtBefore,
                'search': search,
                'status': status,
                'status_updated_at_after': statusUpdatedAtAfter,
                'status_updated_at_before': statusUpdatedAtBefore,
                'ward': ward,
            },
        });
    }

    /**
     * @returns any No response body
     * @throws ApiError
     */
    public adminSurveyTemplatesResultsRetrieve({
        id,
        templateId,
    }: {
        /**
         * A unique integer value identifying this Survey.
         */
        id: number,
        templateId: number,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/admin/survey-templates/{template_id}/results/{id}/',
            path: {
                'id': id,
                'template_id': templateId,
            },
        });
    }

    /**
     * @returns binary
     * @throws ApiError
     */
    public adminSurveyTemplatesResultsExportRetrieve({
        templateId,
        clinic,
        ordering,
        requestedAtAfter,
        requestedAtBefore,
        search,
        status,
        statusUpdatedAtAfter,
        statusUpdatedAtBefore,
        ward,
    }: {
        templateId: number,
        /**
         * Clinic
         */
        clinic?: Array<number>,
        /**
         * Which field to use when ordering the results.
         */
        ordering?: string,
        /**
         * Date of the survey were requested.
         */
        requestedAtAfter?: string,
        /**
         * Date of the survey were requested.
         */
        requestedAtBefore?: string,
        /**
         * A search term.
         */
        search?: string,
        /**
         * Status
         *
         * * `in_progress` - In Progress
         * * `completed` - Completed
         * * `requested` - Requested
         * * `canceled` - Canceled
         * * `overdue` - Overdue
         */
        status?: Array<'canceled' | 'completed' | 'in_progress' | 'overdue' | 'requested'>,
        /**
         * Date of the survey were completed, canceled or requested.
         */
        statusUpdatedAtAfter?: string,
        /**
         * Date of the survey were completed, canceled or requested.
         */
        statusUpdatedAtBefore?: string,
        /**
         * Ward
         */
        ward?: Array<number>,
    }): CancelablePromise<Blob> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/admin/survey-templates/{template_id}/results/export/',
            path: {
                'template_id': templateId,
            },
            query: {
                'clinic': clinic,
                'ordering': ordering,
                'requested_at_after': requestedAtAfter,
                'requested_at_before': requestedAtBefore,
                'search': search,
                'status': status,
                'status_updated_at_after': statusUpdatedAtAfter,
                'status_updated_at_before': statusUpdatedAtBefore,
                'ward': ward,
            },
        });
    }

    /**
     * V2 API for survey templates
     * @returns PaginatedReadOnlyShortSurveyTemplateList
     * @throws ApiError
     */
    public adminV2SurveyTemplatesList({
        limit,
        offset,
        ordering,
        search,
    }: {
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
        /**
         * Which field to use when ordering the results.
         */
        ordering?: string,
        /**
         * A search term.
         */
        search?: string,
    }): CancelablePromise<PaginatedReadOnlyShortSurveyTemplateList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/admin/v2/survey-templates/',
            query: {
                'limit': limit,
                'offset': offset,
                'ordering': ordering,
                'search': search,
            },
        });
    }

    /**
     * V2 API for survey templates
     * @returns SurveyTemplateSerializerV2
     * @throws ApiError
     */
    public adminV2SurveyTemplatesCreate({
        requestBody,
    }: {
        requestBody?: SurveyTemplateSerializerV2,
    }): CancelablePromise<SurveyTemplateSerializerV2> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/admin/v2/survey-templates/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * V2 API for survey templates
     * @returns SurveyTemplateSerializerV2
     * @throws ApiError
     */
    public adminV2SurveyTemplatesRetrieve({
        id,
    }: {
        /**
         * A unique integer value identifying this Survey Template.
         */
        id: number,
    }): CancelablePromise<SurveyTemplateSerializerV2> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/admin/v2/survey-templates/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * V2 API for survey templates
     * @returns SurveyTemplateSerializerV2
     * @throws ApiError
     */
    public adminV2SurveyTemplatesUpdate({
        id,
        requestBody,
    }: {
        /**
         * A unique integer value identifying this Survey Template.
         */
        id: number,
        requestBody?: SurveyTemplateSerializerV2,
    }): CancelablePromise<SurveyTemplateSerializerV2> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/api/admin/v2/survey-templates/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * V2 API for survey templates
     * @returns SurveyTemplateSerializerV2
     * @throws ApiError
     */
    public adminV2SurveyTemplatesPartialUpdate({
        id,
        requestBody,
    }: {
        /**
         * A unique integer value identifying this Survey Template.
         */
        id: number,
        requestBody?: PatchedSurveyTemplateSerializerV2,
    }): CancelablePromise<SurveyTemplateSerializerV2> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/api/admin/v2/survey-templates/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * V2 API for survey templates
     * @returns void
     * @throws ApiError
     */
    public adminV2SurveyTemplatesDestroy({
        id,
    }: {
        /**
         * A unique integer value identifying this Survey Template.
         */
        id: number,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/api/admin/v2/survey-templates/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * V2 API for managing assignment rules
     * @returns PaginatedSurveyAssignmentRuleList
     * @throws ApiError
     */
    public adminV2SurveyTemplatesAssignmentRulesList({
        templateId,
        limit,
        offset,
    }: {
        templateId: number,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedSurveyAssignmentRuleList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/admin/v2/survey-templates/{template_id}/assignment-rules/',
            path: {
                'template_id': templateId,
            },
            query: {
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * V2 API for managing assignment rules
     * @returns SurveyAssignmentRule
     * @throws ApiError
     */
    public adminV2SurveyTemplatesAssignmentRulesCreate({
        templateId,
        requestBody,
    }: {
        templateId: number,
        requestBody?: SurveyAssignmentRule,
    }): CancelablePromise<SurveyAssignmentRule> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/admin/v2/survey-templates/{template_id}/assignment-rules/',
            path: {
                'template_id': templateId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * V2 API for managing assignment rules
     * @returns SurveyAssignmentRule
     * @throws ApiError
     */
    public adminV2SurveyTemplatesAssignmentRulesRetrieve({
        id,
        templateId,
    }: {
        id: number,
        templateId: number,
    }): CancelablePromise<SurveyAssignmentRule> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/admin/v2/survey-templates/{template_id}/assignment-rules/{id}/',
            path: {
                'id': id,
                'template_id': templateId,
            },
        });
    }

    /**
     * V2 API for managing assignment rules
     * @returns SurveyAssignmentRule
     * @throws ApiError
     */
    public adminV2SurveyTemplatesAssignmentRulesUpdate({
        id,
        templateId,
        requestBody,
    }: {
        id: number,
        templateId: number,
        requestBody?: SurveyAssignmentRule,
    }): CancelablePromise<SurveyAssignmentRule> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/api/admin/v2/survey-templates/{template_id}/assignment-rules/{id}/',
            path: {
                'id': id,
                'template_id': templateId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * V2 API for managing assignment rules
     * @returns SurveyAssignmentRule
     * @throws ApiError
     */
    public adminV2SurveyTemplatesAssignmentRulesPartialUpdate({
        id,
        templateId,
        requestBody,
    }: {
        id: number,
        templateId: number,
        requestBody?: PatchedSurveyAssignmentRule,
    }): CancelablePromise<SurveyAssignmentRule> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/api/admin/v2/survey-templates/{template_id}/assignment-rules/{id}/',
            path: {
                'id': id,
                'template_id': templateId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Override "destroy" to properly handle polymorphic trigger deletion.
     * With django-polymorphic 4.5.0+, queryset.delete() correctly handles
     * polymorphic models thanks to PolymorphicGuard.
     * Also, deactivates the template if no rules with coverage remain.
     *
     * Uses transaction.atomic and select_for_update to ensure atomic deletion
     * and prevent deadlocks with concurrent Celery tasks processing rules.
     * @returns void
     * @throws ApiError
     */
    public adminV2SurveyTemplatesAssignmentRulesDestroy({
        id,
        templateId,
    }: {
        id: number,
        templateId: number,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/api/admin/v2/survey-templates/{template_id}/assignment-rules/{id}/',
            path: {
                'id': id,
                'template_id': templateId,
            },
        });
    }

    /**
     * ViewSet for managing episode relative triggers
     * @returns PaginatedEpisodeRelativeTriggerList
     * @throws ApiError
     */
    public adminV2SurveyTemplatesAssignmentRulesEpisodeRelativeTriggersList({
        ruleId,
        templateId,
        limit,
        offset,
    }: {
        ruleId: number,
        templateId: number,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedEpisodeRelativeTriggerList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/admin/v2/survey-templates/{template_id}/assignment-rules/{rule_id}/episode-relative-triggers/',
            path: {
                'rule_id': ruleId,
                'template_id': templateId,
            },
            query: {
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * ViewSet for managing episode relative triggers
     * @returns EpisodeRelativeTrigger
     * @throws ApiError
     */
    public adminV2SurveyTemplatesAssignmentRulesEpisodeRelativeTriggersCreate({
        ruleId,
        templateId,
        requestBody,
    }: {
        ruleId: number,
        templateId: number,
        requestBody: EpisodeRelativeTrigger,
    }): CancelablePromise<EpisodeRelativeTrigger> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/admin/v2/survey-templates/{template_id}/assignment-rules/{rule_id}/episode-relative-triggers/',
            path: {
                'rule_id': ruleId,
                'template_id': templateId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * ViewSet for managing episode relative triggers
     * @returns EpisodeRelativeTrigger
     * @throws ApiError
     */
    public adminV2SurveyTemplatesAssignmentRulesEpisodeRelativeTriggersRetrieve({
        id,
        ruleId,
        templateId,
    }: {
        id: number,
        ruleId: number,
        templateId: number,
    }): CancelablePromise<EpisodeRelativeTrigger> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/admin/v2/survey-templates/{template_id}/assignment-rules/{rule_id}/episode-relative-triggers/{id}/',
            path: {
                'id': id,
                'rule_id': ruleId,
                'template_id': templateId,
            },
        });
    }

    /**
     * ViewSet for managing episode relative triggers
     * @returns EpisodeRelativeTrigger
     * @throws ApiError
     */
    public adminV2SurveyTemplatesAssignmentRulesEpisodeRelativeTriggersUpdate({
        id,
        ruleId,
        templateId,
        requestBody,
    }: {
        id: number,
        ruleId: number,
        templateId: number,
        requestBody: EpisodeRelativeTrigger,
    }): CancelablePromise<EpisodeRelativeTrigger> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/api/admin/v2/survey-templates/{template_id}/assignment-rules/{rule_id}/episode-relative-triggers/{id}/',
            path: {
                'id': id,
                'rule_id': ruleId,
                'template_id': templateId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * ViewSet for managing episode relative triggers
     * @returns EpisodeRelativeTrigger
     * @throws ApiError
     */
    public adminV2SurveyTemplatesAssignmentRulesEpisodeRelativeTriggersPartialUpdate({
        id,
        ruleId,
        templateId,
        requestBody,
    }: {
        id: number,
        ruleId: number,
        templateId: number,
        requestBody?: PatchedEpisodeRelativeTrigger,
    }): CancelablePromise<EpisodeRelativeTrigger> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/api/admin/v2/survey-templates/{template_id}/assignment-rules/{rule_id}/episode-relative-triggers/{id}/',
            path: {
                'id': id,
                'rule_id': ruleId,
                'template_id': templateId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * ViewSet for managing episode relative triggers
     * @returns void
     * @throws ApiError
     */
    public adminV2SurveyTemplatesAssignmentRulesEpisodeRelativeTriggersDestroy({
        id,
        ruleId,
        templateId,
    }: {
        id: number,
        ruleId: number,
        templateId: number,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/api/admin/v2/survey-templates/{template_id}/assignment-rules/{rule_id}/episode-relative-triggers/{id}/',
            path: {
                'id': id,
                'rule_id': ruleId,
                'template_id': templateId,
            },
        });
    }

    /**
     * ViewSet for managing recurring triggers
     * @returns PaginatedRecurringTriggerList
     * @throws ApiError
     */
    public adminV2SurveyTemplatesAssignmentRulesRecurringTriggersList({
        ruleId,
        templateId,
        limit,
        offset,
    }: {
        ruleId: number,
        templateId: number,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedRecurringTriggerList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/admin/v2/survey-templates/{template_id}/assignment-rules/{rule_id}/recurring-triggers/',
            path: {
                'rule_id': ruleId,
                'template_id': templateId,
            },
            query: {
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * ViewSet for managing recurring triggers
     * @returns RecurringTrigger
     * @throws ApiError
     */
    public adminV2SurveyTemplatesAssignmentRulesRecurringTriggersCreate({
        ruleId,
        templateId,
        requestBody,
    }: {
        ruleId: number,
        templateId: number,
        requestBody: RecurringTrigger,
    }): CancelablePromise<RecurringTrigger> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/admin/v2/survey-templates/{template_id}/assignment-rules/{rule_id}/recurring-triggers/',
            path: {
                'rule_id': ruleId,
                'template_id': templateId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * ViewSet for managing recurring triggers
     * @returns RecurringTrigger
     * @throws ApiError
     */
    public adminV2SurveyTemplatesAssignmentRulesRecurringTriggersRetrieve({
        id,
        ruleId,
        templateId,
    }: {
        id: number,
        ruleId: number,
        templateId: number,
    }): CancelablePromise<RecurringTrigger> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/admin/v2/survey-templates/{template_id}/assignment-rules/{rule_id}/recurring-triggers/{id}/',
            path: {
                'id': id,
                'rule_id': ruleId,
                'template_id': templateId,
            },
        });
    }

    /**
     * ViewSet for managing recurring triggers
     * @returns RecurringTrigger
     * @throws ApiError
     */
    public adminV2SurveyTemplatesAssignmentRulesRecurringTriggersUpdate({
        id,
        ruleId,
        templateId,
        requestBody,
    }: {
        id: number,
        ruleId: number,
        templateId: number,
        requestBody: RecurringTrigger,
    }): CancelablePromise<RecurringTrigger> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/api/admin/v2/survey-templates/{template_id}/assignment-rules/{rule_id}/recurring-triggers/{id}/',
            path: {
                'id': id,
                'rule_id': ruleId,
                'template_id': templateId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * ViewSet for managing recurring triggers
     * @returns RecurringTrigger
     * @throws ApiError
     */
    public adminV2SurveyTemplatesAssignmentRulesRecurringTriggersPartialUpdate({
        id,
        ruleId,
        templateId,
        requestBody,
    }: {
        id: number,
        ruleId: number,
        templateId: number,
        requestBody?: PatchedRecurringTrigger,
    }): CancelablePromise<RecurringTrigger> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/api/admin/v2/survey-templates/{template_id}/assignment-rules/{rule_id}/recurring-triggers/{id}/',
            path: {
                'id': id,
                'rule_id': ruleId,
                'template_id': templateId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * ViewSet for managing recurring triggers
     * @returns void
     * @throws ApiError
     */
    public adminV2SurveyTemplatesAssignmentRulesRecurringTriggersDestroy({
        id,
        ruleId,
        templateId,
    }: {
        id: number,
        ruleId: number,
        templateId: number,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/api/admin/v2/survey-templates/{template_id}/assignment-rules/{rule_id}/recurring-triggers/{id}/',
            path: {
                'id': id,
                'rule_id': ruleId,
                'template_id': templateId,
            },
        });
    }

    /**
     * Preview upcoming survey creations
     * Preview upcoming survey creations for a template with AUTO assignment rules and triggers. Returns a paginated list of candidate episodes with scheduled occurrences and aggregate counts. Each item shows when a survey would be created, which rule and trigger would cause it, and whether it's ready to be created now.
     * @returns SurveyAssignmentPreviewResponse Paginated list of preview items with aggregate counts
     * @throws ApiError
     */
    public adminV2SurveyTemplatesPreviewRetrieve({
        templateId,
    }: {
        /**
         * ID of the survey template
         */
        templateId: number,
    }): CancelablePromise<SurveyAssignmentPreviewResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/admin/v2/survey-templates/{template_id}/preview/',
            path: {
                'template_id': templateId,
            },
        });
    }

    /**
     * Get available wards
     * Returns all wards with availability status. If template_id query parameter is provided, wards already covered by rules for that template are marked as unavailable. If rule_id is provided, wards from that rule are excluded from unavailable list (useful when editing an existing rule). If template_id is not provided, all wards are marked as available.
     * @returns AvailableWard
     * @throws ApiError
     */
    public adminV2SurveyTemplatesAvailableWardsList({
        ruleId,
        templateId,
    }: {
        /**
         * Optional ID of the rule being edited (to exclude its wards from unavailable list)
         */
        ruleId?: number,
        /**
         * Optional ID of the survey template to check availability against
         */
        templateId?: number,
    }): CancelablePromise<Array<AvailableWard>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/admin/v2/survey-templates/available-wards/',
            query: {
                'rule_id': ruleId,
                'template_id': templateId,
            },
        });
    }

}
